import { dict, PANEL_LANGS, type PanelLang } from './lang';
import { loginHref } from './loginHref';

/**
 * Lớp gọi API duy nhất của giao diện.
 *
 * Mọi thứ đi qua đây vì hai lý do không thể để rơi: token CSRF phải kèm theo
 * mỗi thao tác ghi, và thông điệp lỗi của máy chủ phải tới được chỗ hiển thị.
 * Gọi `fetch` thẳng ở component thì sớm muộn sẽ có một chỗ quên một trong hai —
 * và cả hai đều chỉ lộ ra khi có người dùng thật bấm nút.
 */

const TEXT = dict({
  en: {
    sessionExpired: 'Your session has expired. Redirecting to the sign-in page…',
    serverError: (status: number) => `The server returned error ${status}.`,
    uploadFailed: 'The connection dropped while uploading. Nothing was saved — try again.',
    uploadAborted: 'Upload cancelled.',
  },
  vi: {
    sessionExpired: 'Phiên đã hết hạn. Đang chuyển sang trang đăng nhập…',
    serverError: (status: number) => `Máy chủ trả về lỗi ${status}.`,
    uploadFailed: 'Mất kết nối giữa lúc tải lên. Không có gì được lưu — thử lại.',
    uploadAborted: 'Đã huỷ lượt tải lên.',
  },
});

/**
 * Ngôn ngữ đang chọn, đọc từ thẻ `<html lang>`.
 *
 * KHÔNG dùng `useT` được: `apiFetch` bị gọi từ react-query và từ cả những chỗ
 * ngoài cây component, nên không có hook nào với tới. Truyền ngôn ngữ vào từng
 * lượt gọi cũng không: mỗi component gọi API sẽ phải mang thêm một tham số chỉ
 * để phòng hai câu lỗi hiếm khi hiện.
 *
 * `LanguageProvider` đã ghi sẵn thuộc tính này mỗi lần đổi ngôn ngữ (xem
 * `lib/lang.tsx`), nên đây là đọc lại một giá trị có sẵn chứ không phải dựng
 * thêm một nguồn sự thật thứ hai.
 */
function currentLang(): PanelLang {
  const value = document.documentElement.lang;
  return (PANEL_LANGS as readonly string[]).includes(value) ? (value as PanelLang) : 'en';
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details: readonly string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Gộp thông điệp chính và chi tiết thành một chuỗi hiển thị được. */
  get full(): string {
    return this.details.length === 0 ? this.message : `${this.message}\n• ${this.details.join('\n• ')}`;
  }
}

/**
 * Token CSRF của phiên hiện tại.
 *
 * Giữ ở module scope thay vì trong React state: `apiFetch` được gọi từ cả
 * react-query lẫn các hàm ngoài cây component, và luồng qua props sẽ có chỗ
 * không với tới. `AuthProvider` cập nhật nó mỗi lần `/api/auth/me` trả về.
 */
let csrfToken = '';

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

/** Cửa sổ đang chờ chuyển sang trang đăng nhập — chặn nhiều 401 cùng lúc đá nhiều lần. */
let redirecting = false;

/**
 * Server đang được xem. `''` = chưa chọn, máy chủ tự lấy server đầu tiên mà
 * người này có quyền.
 *
 * KHÔNG còn lựa chọn "tất cả server". Mỗi server bot phục vụ là một sản phẩm
 * độc lập của một bên khác nhau, nên một con số gộp hai cộng đồng vừa vô nghĩa
 * vừa để lộ dữ liệu của bên này cho bên kia. Máy chủ cũng không tin tham số này:
 * nó kiểm tra quyền trên từng request (xem `server/auth/guildScope.ts`), nên
 * sửa localStorage không mở được cửa nào.
 *
 * Giữ ở module scope cùng lý do với token CSRF: lựa chọn này phải đi kèm MỌI
 * lượt gọi, kể cả từ chỗ không với tới React state.
 */
const GUILD_STORAGE_KEY = 'panel-guild';
let selectedGuild = localStorage.getItem(GUILD_STORAGE_KEY) ?? '';

export function selectedGuildId(): string {
  return selectedGuild;
}

export function setSelectedGuildId(guildId: string): void {
  selectedGuild = guildId;
  if (guildId === '') localStorage.removeItem(GUILD_STORAGE_KEY);
  else localStorage.setItem(GUILD_STORAGE_KEY, guildId);
}

/**
 * Gắn `guild=` vào MỌI lượt gọi API, kể cả thao tác ghi.
 *
 * Khác bản trước, nơi chỉ lượt đọc mới mang tham số này: giờ thao tác ghi (cộng
 * XP, chốt chu kỳ, sửa bảng vai trò) cũng phải nói rõ nó nhắm vào server nào —
 * đó chính là những thao tác mà nhắm nhầm server gây hậu quả lớn nhất.
 *
 * Đường auth đứng ngoài: đăng nhập không thuộc server nào.
 */
function withGuildParam(path: string, _method: string): string {
  if (!path.startsWith('/api/') || path.startsWith('/api/auth/')) return path;

  const params: string[] = [];
  if (selectedGuild !== '') params.push(`guild=${encodeURIComponent(selectedGuild)}`);
  /**
   * `lang` đi kèm MỌI lượt gọi, cùng lý do với `guild`.
   *
   * Máy chủ sinh ra rất nhiều câu người dùng đọc — báo lỗi, cảnh báo quyền, ghi
   * chú sau khi lưu — và chúng phải theo ngôn ngữ người xem đang chọn. Trước đây
   * chỉ trang Cấu hình tự gắn `?lang=` bằng tay, nên mọi câu từ mọi endpoint còn
   * lại hiện ra tiếng Việt giữa một giao diện tiếng Anh.
   *
   * Gắn ở ĐÂY chứ không ở từng chỗ gọi: một tham số phải nhớ thêm bằng tay là một
   * tham số sẽ bị quên, và đã bị quên ở mọi endpoint trừ đúng một cái.
   */
  if (path.includes('lang=')) {
    // Chỗ gọi đã tự khai (trang Cấu hình) — tôn trọng, đừng gắn thành hai lần.
  } else {
    params.push(`lang=${currentLang()}`);
  }

  if (params.length === 0) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}${params.join('&')}`;
}

/**
 * Địa chỉ đầy đủ của một endpoint, đã gắn `guild=`.
 *
 * Dành cho hai thứ KHÔNG đi qua `apiFetch`: link tải file (`<a href>`) và lượt
 * tải lên. Cả hai đều phải mang `?guild=` như mọi lượt gọi khác — thiếu nó thì
 * máy chủ lùi về "server đầu tiên người này có quyền", và người đang xem cộng
 * đồng B sẽ tải về bản sao lưu của cộng đồng A mà không có gì báo.
 */
export function apiUrl(path: string): string {
  return withGuildParam(path, 'GET');
}

/**
 * Tải một file LÊN, gửi thân request thô.
 *
 * Không dùng `FormData`: máy chủ đọc thân request thành một dòng và ghi thẳng ra
 * đĩa (xem `addContentTypeParser` trong `server/index.ts`), nên bọc thêm một lớp
 * multipart chỉ để bóc ra ngay sau đó là thêm một bộ phân tích vào đúng đường đi
 * của dữ liệu người ngoài gửi lên.
 *
 * `XMLHttpRequest` chứ không phải `fetch`: nó là cách duy nhất trong trình duyệt
 * đọc được tiến độ tải lên. Với một file vài trăm MB, một nút "Đang tải lên…"
 * đứng yên trong bốn phút không phân biệt được với một nút bị treo.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', apiUrl(path));
    request.setRequestHeader('content-type', 'application/gzip');
    request.setRequestHeader('x-csrf-token', csrfToken);
    request.withCredentials = true;

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    });

    request.addEventListener('load', () => {
      let body: { error?: string; details?: string[] } = {};
      try {
        body = JSON.parse(request.responseText) as typeof body;
      } catch {
        // Phản hồi không phải JSON (proxy chết giữa chừng) — giữ thông điệp mặc định.
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(body as T);
        return;
      }
      reject(
        new ApiError(
          request.status,
          body.error ?? TEXT[currentLang()].serverError(request.status),
          body.details ?? [],
        ),
      );
    });

    request.addEventListener('error', () => {
      reject(new ApiError(0, TEXT[currentLang()].uploadFailed));
    });
    request.addEventListener('abort', () => {
      reject(new ApiError(0, TEXT[currentLang()].uploadAborted));
    });

    request.send(file);
  });
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const method = (rest.method ?? (json === undefined ? 'GET' : 'POST')).toUpperCase();

  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set('content-type', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') headers.set('x-csrf-token', csrfToken);

  const response = await fetch(withGuildParam(path, method), {
    ...rest,
    method,
    headers,
    // Cookie phiên là httpOnly nên trình duyệt tự gửi, nhưng phải khai `same-origin`
    // để nó cũng đi kèm khi chạy qua proxy của Vite ở chế độ dev.
    credentials: 'same-origin',
    body: json === undefined ? rest.body : JSON.stringify(json),
  });

  if (response.status === 401 && !path.startsWith('/api/auth/')) {
    if (!redirecting) {
      redirecting = true;
      window.location.href = loginHref(window.location.pathname);
    }
    throw new ApiError(401, TEXT[currentLang()].sessionExpired);
  }

  if (!response.ok) {
    let message = TEXT[currentLang()].serverError(response.status);
    let details: string[] = [];
    try {
      const body = (await response.json()) as { error?: string; details?: string[] };
      if (typeof body.error === 'string') message = body.error;
      if (Array.isArray(body.details)) details = body.details;
    } catch {
      // Lỗi không phải JSON (proxy chết, bot vừa restart). Giữ thông điệp mặc định.
    }
    throw new ApiError(response.status, message, details);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Dựng query string, bỏ qua giá trị rỗng để URL không đầy `&x=`. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text.length === 0 ? '' : `?${text}`;
}
